import { useCallback } from "react";
import { useAsyncResult } from "../../Hooks/UseAsyncResult";
import { fetchBaseJson, fetchExtensionConfigOpenPrs } from "../../Api/ado.api";
import { useEv2ExperimentParams } from "../UseCurrentExtension";
import { useAppContext } from "../../Contexts";

/**
 * Hook to fetch the base JSON for the current extension.
 * @param setError - Function to set error state in case of fetch failure.
 * @returns The base JSON data or null if not found.
 */
export function useFetchBaseJsonConfig() {
  const { setError } = useAppContext();
  const { extensionFolder } = useEv2ExperimentParams();

  const func = useCallback(
    async (_disposable, _force) => {
      if (!extensionFolder) {
        return null;
      }
      let baseJson;
      const { extensionRepoPath } = extensionFolder;
      try {
        const response = await fetchBaseJson(extensionRepoPath);
        baseJson = response;
      } catch (error) {
        console.error("Failed to fetch base JSON:", error);
      }

      if (!baseJson) {
        return null;
      }

      const normalizedBaseJson = {
        ...baseJson,
        assignmentUnit: baseJson.assignmentUnit,
        environment: baseJson.environment,
      };
      return normalizedBaseJson;
    },
    [extensionFolder],
  );

  return useAsyncResult({
    func,
    setError,
  });
}

/**
 * Hook to fetch the extension configurations in open PRs
 * @returns the extension config from the open pr, prUrl, and extensionName
 */
export function useFetchExtensionConfigOpenPrs() {
  const { setError, extensionName } = useAppContext();
  const func = useCallback(
    async (_disposable) => {
      try {
        const configPr = await fetchExtensionConfigOpenPrs();
        if (!configPr || configPr.extensionName !== extensionName) {
          return null;
        }
        return configPr;
      } catch (error) {
        setError("Failed to fetch extension configuration PRs");
        return null;
      }
    },
    [setError, extensionName],
  );

  return useAsyncResult({
    func,
    setError,
  });
}
